import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../services/apiClient';
import StakeTable from './GameEngine/StakeTable';
import { Button, FormInput, FormSelect, Badge } from '../components/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Calendar, X } from 'lucide-react';

interface DetailReportItem {
  record_id: number;
  rule_id: number;
  player_id: number;
  player_name: string;
  player_email: string;
  registered_at?: string;
  total_deposit_usd: number;
  total_wager_usd: number;
  is_qualified: boolean;
  commission_earned: number;
  payment_status: string;
  paid_at: string | null;
  min_deposit_usd: number;
  min_wager_usd: number;
  start_date: string;
  end_date: string;
}

interface DetailsResponse {
  data: DetailReportItem[];
  total: number;
}

type DatePreset = "today" | "yesterday" | "week" | "month" | "custom" | "all";

const formatDate = (d: Date) => d.toISOString().split("T")[0]; // YYYY-MM-DD

const getPresetRange = (
  preset: DatePreset
): { start: string | null; end: string | null } => {
  const now = new Date();
  const todayStr = formatDate(now);

  if (preset === "today") {
    return { start: todayStr, end: todayStr };
  }
  if (preset === "yesterday") {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    const s = formatDate(y);
    return { start: s, end: s };
  }
  if (preset === "week") {
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay()); // Sunday
    return { start: formatDate(start), end: todayStr };
  }
  if (preset === "month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: formatDate(start), end: todayStr };
  }
  return { start: null, end: null };
};

const fetchDetailsAPI = async (filters: { affiliateId: number; startDate: string; endDate: string; dateType: string; paymentStatus: string; isQualified: string; page: number; limit: number; search: string }): Promise<DetailsResponse> => {
  const token = localStorage.getItem('token');
  const params = new URLSearchParams({
    affiliateId: String(filters.affiliateId),
    startDate: filters.startDate,
    endDate: filters.endDate,
    dateType: filters.dateType,
    paymentStatus: filters.paymentStatus,
    isQualified: filters.isQualified,
    page: String(filters.page),
    limit: String(filters.limit),
    search: filters.search
  });
  const res = await apiClient.get(`/admin/affiliate-payouts/report/details?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data.success ? { data: res.data.data, total: res.data.total } : { data: [], total: 0 };
};

const AffiliatePayoutReportDetails: React.FC = () => {
  const { affiliateId } = useParams<{ affiliateId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const role = (localStorage.getItem('role') || '').toLowerCase().trim();
  const canMarkPaid = role === 'admin' || role === 'manager';

  const initialStartDate = searchParams.get('startDate') || '';
  const initialEndDate = searchParams.get('endDate') || '';
  const initialDateType = searchParams.get('dateType') || 'refer';

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [limit, setLimit] = useState(25);
  
  const [activePreset, setActivePreset] = useState<DatePreset>(
    initialStartDate || initialEndDate ? 'custom' : 'all'
  );
  const [dateType, setDateType] = useState(initialDateType);
  const [filterStartDate, setFilterStartDate] = useState(initialStartDate);
  const [filterEndDate, setFilterEndDate] = useState(initialEndDate);
  const [paymentStatus, setPaymentStatus] = useState('all');
  const [isQualified, setIsQualified] = useState('all');

  const affIdNum = parseInt(affiliateId || '0');

  const { data = { data: [], total: 0 }, isLoading, refetch } = useQuery({
    queryKey: ['payoutReportDetails', affIdNum, page, limit, searchTerm, filterStartDate, filterEndDate, dateType, paymentStatus, isQualified],
    queryFn: () => fetchDetailsAPI({
      affiliateId: affIdNum,
      startDate: filterStartDate,
      endDate: filterEndDate,
      dateType,
      paymentStatus,
      isQualified,
      page,
      limit,
      search: searchTerm
    }),
    enabled: affIdNum > 0
  });

  const detailsItems = data.data;
  const total = data.total;

  const markPaidMutation = useMutation({
    mutationFn: async (payload: { ruleId: number; playerId: number; status?: string }) => {
      const token = localStorage.getItem('token');
      return apiClient.post('/admin/affiliate-payouts/mark-paid', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payoutReportDetails'] });
      queryClient.invalidateQueries({ queryKey: ['payoutReport'] });
      toast.success("Payout marked as paid successfully!");
      refetch();
    },
    onError: () => {
      toast.error("Failed to mark payout as paid");
    }
  });

  const handlePresetChange = (preset: DatePreset) => {
    setActivePreset(preset);
    if (preset === 'custom') {
      return;
    }
    const { start, end } = getPresetRange(preset);
    setFilterStartDate(start || '');
    setFilterEndDate(end || '');
    setPage(1);
  };

  const handleClearFilter = () => {
    setActivePreset("all");
    setFilterStartDate("");
    setFilterEndDate("");
    setPaymentStatus("all");
    setIsQualified("all");
    setPage(1);
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        affiliateId: String(affIdNum),
        startDate: filterStartDate,
        endDate: filterEndDate,
        dateType,
        paymentStatus,
        isQualified,
        search: searchTerm,
        export: 'true'
      });
      const res = await apiClient.get(`/admin/affiliate-payouts/report/details?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.data.success || !res.data.data || res.data.data.length === 0) {
        toast.info("No data to export");
        return;
      }
      const dataToExport = res.data.data;

      const headers = ["Player Name", "Player Email", "Registration Date", "Deposits (USD)", "Min Deposit Threshold", "Wagered (USD)", "Min Wager Threshold", "Qualified", "Commission (USD)", "Status", "Paid At", "Rule Start Date", "Rule End Date"];
      const csvRows = [headers.join(",")];

      for (const item of dataToExport) {
        const row = [
          `"${(item.player_name || '').replace(/"/g, '""')}"`,
          `"${(item.player_email || '').replace(/"/g, '""')}"`,
          item.registered_at ? new Date(item.registered_at).toLocaleString() : "-",
          item.total_deposit_usd.toFixed(2),
          item.min_deposit_usd.toFixed(2),
          item.total_wager_usd.toFixed(2),
          item.min_wager_usd.toFixed(2),
          item.is_qualified ? "Yes" : "No",
          item.commission_earned.toFixed(2),
          item.payment_status.toUpperCase(),
          item.paid_at ? new Date(item.paid_at).toLocaleString() : "-",
          new Date(item.start_date).toLocaleDateString(),
          new Date(item.end_date).toLocaleDateString()
        ];
        csvRows.push(row.join(","));
      }

      const csvString = csvRows.join("\n");
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `referred_players_details_${affIdNum}_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
      toast.error("Failed to export data");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const columns = [
    { key: 'player_name', label: 'Player' },
    { key: 'player_email', label: 'Email' },
    { key: 'registered_at', label: 'Registration Date', render: (val: string) => val ? new Date(val).toLocaleString() : '-' },
    { key: 'total_deposit_usd', label: 'Deposits', render: (val: number, item: DetailReportItem) => `$${val.toFixed(2)} / $${item.min_deposit_usd.toFixed(2)}` },
    { key: 'total_wager_usd', label: 'Wagered', render: (val: number, item: DetailReportItem) => `$${val.toFixed(2)} / $${item.min_wager_usd.toFixed(2)}` },
    { key: 'is_qualified', label: 'Qualified', render: (val: boolean) => <Badge variant={val ? 'success' : 'danger'}>{val ? 'Yes' : 'No'}</Badge> },
    { key: 'commission_earned', label: 'Commission', render: (val: number) => `$${val.toFixed(2)}` },
    { key: 'payment_status', label: 'Payout Status', render: (val: string) => <Badge variant={val === 'paid' ? 'success' : 'warning'}>{val.toUpperCase()}</Badge> },
    ...(canMarkPaid ? [{
      key: 'actions', label: 'Action', textAlign: 'right' as const,
      render: (_: any, item: DetailReportItem) => {
        if (!item.is_qualified) return '-';
        const isPaid = item.payment_status === 'paid';
        return (
          <Button 
            variant={isPaid ? 'danger' : 'success'} 
            size="xs" 
            loading={markPaidMutation.isPending} 
            onClick={async () => {
              await markPaidMutation.mutateAsync({ 
                ruleId: item.rule_id, 
                playerId: item.player_id,
                status: isPaid ? 'unpaid' : 'paid'
              });
            }}
          >
            {isPaid ? 'Mark Unpaid' : 'Mark Paid'}
          </Button>
        );
      }
    }] : [])
  ];

  const PRESETS: { label: string; value: DatePreset }[] = [
    { label: "All Time", value: "all" },
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "This Week", value: "week" },
    { label: "This Month", value: "month" },
    { label: "Custom Range", value: "custom" },
  ];

  const isAffiliate = role === 'affiliate';

  return (
    <div className="ml-0 lg:ml-[256px] p-4 sm:p-6 min-h-screen text-white bg-[#151929]">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          {!isAffiliate && (
            <Button variant="ghost" size="sm" onClick={() => navigate(`/affiliate-payouts/report?startDate=${filterStartDate}&endDate=${filterEndDate}&dateType=${dateType}`)} className="mb-2">
              &larr; Back to Report
            </Button>
          )}
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
            {isAffiliate ? "My Referred Players & Commission Report" : `Referred Players - Affiliate ID #${affIdNum}`}
          </h2>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-end gap-4 bg-slate-900 border border-slate-800 p-4 rounded-xl mb-4">
        {/* Date Type Selector */}
        <div className="w-full sm:w-44">
          <FormSelect
            label="Date Type"
            options={[
              { value: 'refer', label: 'Refer Date' },
              { value: 'deposit', label: 'Deposit Date' },
              { value: 'wager', label: 'Wager Date' }
            ]}
            value={dateType}
            onChange={e => { setDateType(e.target.value); setPage(1); }}
          />
        </div>

        {/* Date Preset Selector */}
        <div className="w-full sm:w-44">
          <FormSelect
            label="Date Preset"
            options={PRESETS}
            value={activePreset}
            onChange={e => handlePresetChange(e.target.value as DatePreset)}
          />
        </div>

        {/* Payout Status Selector */}
        <div className="w-full sm:w-40">
          <FormSelect
            label="Payout Status"
            options={[
              { value: 'all', label: 'All Statuses' },
              { value: 'paid', label: 'Paid' },
              { value: 'unpaid', label: 'Unpaid' }
            ]}
            value={paymentStatus}
            onChange={e => { setPaymentStatus(e.target.value); setPage(1); }}
          />
        </div>

        {/* Qualification Selector */}
        <div className="w-full sm:w-44">
          <FormSelect
            label="Qualification Status"
            options={[
              { value: 'all', label: 'All Qualifications' },
              { value: 'true', label: 'Eligible (Qualified)' },
              { value: 'false', label: 'Not Eligible' }
            ]}
            value={isQualified}
            onChange={e => { setIsQualified(e.target.value); setPage(1); }}
          />
        </div>

        {activePreset === 'custom' && (
          <>
            <div className="w-full sm:w-40">
              <FormInput 
                type="date" 
                label="Start Date" 
                value={filterStartDate}
                onChange={e => { setFilterStartDate(e.target.value); setPage(1); }}
                className="[color-scheme:dark]"
              />
            </div>
            <div className="w-full sm:w-40">
              <FormInput 
                type="date" 
                label="End Date" 
                value={filterEndDate}
                onChange={e => { setFilterEndDate(e.target.value); setPage(1); }}
                className="[color-scheme:dark]"
              />
            </div>
          </>
        )}

        {(activePreset !== "all" || paymentStatus !== "all" || isQualified !== "all") && (
          <button
            onClick={handleClearFilter}
            className="
              flex items-center justify-center gap-1 px-4 py-2.5 rounded-xl text-sm font-medium h-10 mb-0.5
              bg-red-500/10 border border-red-500/30 text-red-400
              hover:bg-red-500/20 hover:text-red-300 transition-all duration-200
            "
          >
            <X size={14} />
            Clear
          </button>
        )}
      </div>

      <StakeTable
        title=""
        columns={columns}
        data={detailsItems}
        isLoading={isLoading}
        searchPlaceholder="Search player by name or email..."
        onSearch={(q) => { setSearchTerm(q); setPage(1); }}
        page={page}
        totalPages={totalPages}
        total={total}
        limit={limit}
        onLimitChange={setLimit}
        onPageChange={setPage}
        selectable={false}
        emptyMessage="No referred players found."
        actions={
          <Button variant="ghost" onClick={handleExportCSV}>Export CSV</Button>
        }
        hasSidebarMargin={false}
      />
    </div>
  );
};

export default AffiliatePayoutReportDetails;
